import PianoInlineTemplateContainer from '@/main/components/PianoInlineTemplateContainer';
import { useOptimizely, type VariantProps } from 'providers/OptimizelyProvider';
import Outbrain from 'components/Outbrain';
import type { InStreamListConfig } from 'interfaces/ads/Ad';
import type { PageType } from 'interfaces/content/articles/Post';
import type { JSX } from 'react';
import { calculateWhereToInsertEmbeds, insertElementsIntoTree } from 'utils/miscUtils/childrenUtils';
import InStream from './InStream';

// insertAds now also inserts Piano Inline Template Container at a target ad position
const insertAdsAndPianoTemplate = (
  elements: JSX.Element,
  frequency: number,
  renderAds: boolean,
  articleUrl: string,
  section: string,
  suppressInStreamAds?: boolean,
  lastPWithoutAds?: number,
  pageType?: PageType,
) => {
  const { userFlags: userVariants } = useOptimizely();
  const pianoTemplateLocationTest = userVariants.pianoTemplateLocation as VariantProps | undefined;
  const pianoLocationTestEnabled = pianoTemplateLocationTest?.enabled ?? false;

  let adNumber = 0;
  // OptimizelyProvider test to change which ad the Piano Inline Template Container appears after
  if (frequency === 0) {
    return elements;
  }

  const insertAdAfter = calculateWhereToInsertEmbeds(elements, frequency, lastPWithoutAds);
  const insertElementsArray = insertAdAfter.map((after, index) => {
    adNumber += 1;
    const shouldInsertPianoTemplateHere = pianoLocationTestEnabled && adNumber === 3;

    if (process.env.FEATURE_FLAG_INLINE_OUTBRAIN && index === 0 && ['Lifestyle', 'Retail'].includes(section)) {
      return {
        after,
        node: (
          <>
            <Outbrain
              placement='inline'
              replacedAdIndex={index}
              section={section}
              permalink={articleUrl}
            />
            {shouldInsertPianoTemplateHere && <PianoInlineTemplateContainer />}
          </>
        ),
      };
    }
    return {
      after,
      node: (
        <>
          <InStream
            index={index}
            // eslint-disable-next-line react/no-array-index-key
            key={`ad-${index}`}
            delay={0}
            suppressInStreamAds={suppressInStreamAds}
            renderAds={renderAds}
            pageType={pageType}
            addPadding
          />
          {shouldInsertPianoTemplateHere && <PianoInlineTemplateContainer />}
        </>
      ),
    };
  });

  if (adNumber < 3) {
    const newElements = (
      <>
        {elements}
        {pianoLocationTestEnabled && <PianoInlineTemplateContainer />}
      </>
    );
    return insertElementsIntoTree(newElements, insertElementsArray);
  }

  return insertElementsIntoTree(elements, insertElementsArray);
};

const InStreamList = ({
  articleUrl,
  section,
  frequency,
  children,
  lastPWithoutAds,
  suppressInStreamAds,
  renderAds,
  pageType,
}: InStreamListConfig) => {
  return (
    <div>
      {insertAdsAndPianoTemplate(
        children,
        frequency,
        renderAds,
        articleUrl,
        section,
        suppressInStreamAds,
        lastPWithoutAds,
        pageType,
      )}
    </div>
  );
};

export default InStreamList;
